import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Wallet, Percent, TrendingUp, Receipt, Undo2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

const Stat = ({ label, value, Icon, tone, testId }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={testId}>
    <div className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em] ${tone}`}>
      <Icon size={14} /> {label}
    </div>
    <p className="text-xl md:text-2xl font-black text-white mt-2">{value} F</p>
  </div>
);

const SaleRow = ({ s }) => {
  const reversal = s.type === "partner_sale_reversal";
  return (
    <li className="flex items-center justify-between gap-3 py-3 border-b border-white/5 last:border-0" data-testid={`wallet-sale-${s.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${reversal ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400"}`}>
          {reversal ? <Undo2 size={16} /> : <Receipt size={16} />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">
            {reversal ? "Annulation" : "Vente"} #{(s.order_id || "").slice(0, 8)} {s.customer_name ? `· ${s.customer_name}` : ""}
          </p>
          <p className="text-[11px] text-slate-400">
            {fmtDate(s.created_at)}
            {!reversal && ` · ${fmt(s.gross_amount)} F − ${fmt(s.commission)} F commission`}
          </p>
        </div>
      </div>
      <span className={`text-sm font-black flex-shrink-0 ${reversal ? "text-red-400" : "text-emerald-400"}`}>
        {reversal ? "" : "+"}{fmt(s.net_amount)} F
      </span>
    </li>
  );
};

export const PartnerWalletPanel = ({ partnerId }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/partners/${partnerId}/sales`);
      setData(r.data);
    } catch {
      setData({ wallet_balance: 0, totals: {}, sales: [] });
    }
  }, [partnerId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="h-40 rounded-[2rem] bg-white/5 animate-pulse" data-testid="partner-wallet-loading" />;
  const t = data.totals || {};

  return (
    <section className="bg-[#0F172A] border border-[#D4AF37]/30 rounded-[2rem] p-5 md:p-7 space-y-5" data-testid="partner-wallet-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#D4AF37]">Mon portefeuille</p>
          <p className="text-3xl md:text-4xl font-black text-white mt-1" data-testid="partner-wallet-balance">{fmt(data.wallet_balance)} FCFA</p>
        </div>
        <p className="text-[11px] text-slate-400 max-w-xs">Chaque vente est créditée dès la confirmation, moins 10 % de commission ARTEKNOS.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label={`Ventes (${t.sales_count || 0})`} value={fmt(t.gross)} Icon={TrendingUp} tone="text-blue-300" testId="partner-wallet-gross" />
        <Stat label="Commission 10 %" value={fmt(t.commission)} Icon={Percent} tone="text-amber-300" testId="partner-wallet-commission" />
        <Stat label="Net reçu" value={fmt(t.net)} Icon={Wallet} tone="text-emerald-300" testId="partner-wallet-net" />
      </div>
      {data.sales.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4" data-testid="partner-wallet-empty">Aucune vente pour le moment.</p>
      ) : (
        <ul className="max-h-80 overflow-y-auto pr-1" data-testid="partner-wallet-sales-list">
          {data.sales.map((s) => <SaleRow key={s.id} s={s} />)}
        </ul>
      )}
    </section>
  );
};
